import React, { useEffect, useRef, useState } from "react";
import { useLocation, Link } from "wouter";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { getSidebarRoutes } from "@/config/routes";
import { Badge } from "@/components/ui/badge";
import { ChevronRight, ChevronLeft, X } from "lucide-react";
import { BadeelLogo } from "@/components/shared/badeel-logo";
import { useIsMobileOrTablet } from "@/hooks/use-mobile";

export interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const SIDEBAR_COLLAPSED_KEY = "sidebar-collapsed";

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const [location] = useLocation();
  const { t } = useTranslation();
  const routes = getSidebarRoutes();
  const isMobileOrTablet = useIsMobileOrTablet();
  const sidebarRef = useRef<HTMLDivElement>(null);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  
  // Collapsed state - only for desktop
  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
      return saved === 'true';
    }
    return false;
  });

  // Toggle collapsed state
  const toggleCollapsed = () => {
    setIsCollapsed((prev) => {
      const newValue = !prev;
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(newValue));
      return newValue;
    });
  };

  // Function to get proper translation key for route labels
  const getTranslationKey = (label: string) => {
    const keyMap: { [key: string]: string } = {
      'Dashboard Reports': 'reports',
      'Product Manage': 'products',
      'Categories Manage': 'categories',
      'Brands Manage': 'brands',
      'Orders Manage': 'orders',
      'User Manage': 'userManagement',
      'Payments Manage': 'paymentTransactions',
      'Notification Manage': 'notifications',
      'لوحة التحكم': 'dashboard',
      'Analytics': 'analytics',
      'Rooms': 'rooms',
      'Bookings': 'bookings',
      'User Management': 'userManagement',
      'Reviews': 'reviews',
      'Coupons & Promotions': 'coupons',
      'Payments • Transactions': 'paymentTransactions',
      'Payments • Logs': 'paymentLogs',
      'Support': 'support',
      'Reports': 'reports',
      'Notifications': 'notifications',
      'Static Content': 'staticContent',
      'Settings': 'settings',
      'Profile Settings': 'profileSettings',
      'Guests': 'guests',
      'Housekeeping': 'housekeeping',
      'Maintenance': 'maintenance',
      'Users': 'users'
    };
    return keyMap[label] || label.toLowerCase().replace(/\s+/g, '');
  };

  // Handle escape key to close sidebar and focus management
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && isMobileOrTablet) {
        onClose();
      }
    };

    if (isOpen && isMobileOrTablet) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when sidebar is open on mobile/tablet
      document.body.style.overflow = 'hidden';
      
      // Focus management - focus the first focusable element
      const firstFocusable = sidebarRef.current?.querySelector(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      ) as HTMLElement;
      
      if (firstFocusable) {
        firstFocusable.focus();
      }
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, isMobileOrTablet, onClose]);

  // Handle click outside to close sidebar
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target as Node) && isOpen && isMobileOrTablet) {
        onClose();
      }
    };

    if (isOpen && isMobileOrTablet) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, isMobileOrTablet, onClose]);

  // Touch gesture handling
  const minSwipeDistance = 50;

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && isOpen && isMobileOrTablet) {
      onClose();
    }
  };

  return (
    <>
      {/* Mobile/Tablet overlay */}
      {isOpen && isMobileOrTablet && (
        <div
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm transition-all duration-300 ease-in-out"
          onClick={onClose}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          aria-hidden="true"
          style={{
            animation: 'fadeIn 0.3s ease-in-out'
          }}
        />
      )}
      
      <div
        ref={sidebarRef}
        className={cn(
          "sidebar-component fixed inset-y-0 left-0 z-50 bg-sidebar text-sidebar-foreground shadow-lg transition-all duration-300 ease-in-out w-full",
          // Mobile: full width with slide animation
          isMobileOrTablet ? "max-w-xs sm:max-w-sm" : "",
          // Desktop: width based on collapsed state
          !isMobileOrTablet && (isCollapsed ? "w-16" : "w-64"),
          // Desktop: static positioning
          !isMobileOrTablet && "lg:static lg:inset-0 lg:translate-x-0",
          // Mobile/Tablet: slide animation
          isMobileOrTablet && (isOpen ? "translate-x-0" : "-translate-x-full"),
          // Focus management
          isOpen && "focus-within:outline-none"
        )}
        role="navigation"
        aria-label="Main navigation"
        aria-hidden={!isOpen}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <div className="flex h-full flex-col">
          {/* Header */}
          <div className={cn(
            "flex h-16 items-center border-b border-sidebar-border transition-all duration-300",
            isMobileOrTablet ? "justify-between px-4 sm:px-6" : isCollapsed ? "justify-center px-2" : "justify-between px-4 sm:px-6"
          )}>
            {!isMobileOrTablet && isCollapsed ? (
              <Link href="/dashboard" onClick={onClose} className="flex items-center justify-center">
                <BadeelLogo size="sm" showText={false} />
              </Link>
            ) : (
              <Link href="/dashboard" onClick={onClose} className="flex items-center">
                <BadeelLogo size="sm" showText={!isCollapsed} />
              </Link>
            )}
            
            <div className="flex items-center gap-2">
              {/* Toggle collapse button for desktop */}
              {!isMobileOrTablet && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 transition-colors hover:bg-sidebar-accent/50"
                  onClick={toggleCollapsed}
                  aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                  title={isCollapsed ? t("expandSidebar") || "Expand sidebar" : t("collapseSidebar") || "Collapse sidebar"}
                >
                  {isCollapsed ? (
                    <ChevronRight className="h-4 w-4" />
                  ) : (
                    <ChevronLeft className="h-4 w-4" />
                  )}
                </Button>
              )}
              
              {/* Close button for mobile/tablet */}
              <Button
                variant="ghost"
                size="icon"
                className={cn(
                  "h-8 w-8 transition-colors hover:bg-sidebar-accent/50",
                  isMobileOrTablet ? "block" : "lg:hidden"
                )}
                onClick={onClose}
                aria-label="Close sidebar"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Navigation */}
          <ScrollArea className={cn(
            "flex-1",
            isMobileOrTablet ? "px-3 sm:px-4" : isCollapsed ? "px-1" : "px-3 sm:px-4"
          )}>
            <nav className="mt-4 sm:mt-6" role="menubar" aria-label="Main navigation menu">
              <div className="space-y-1" role="group">
                {routes.map((route) => {
                  const isActive = location === route.path || (route.path === "/dashboard" && location === "/");
                  return (
                    <Link key={route.path} href={route.path}>
                      <div
                        className={cn(
                          "group flex items-center rounded-lg transition-all duration-200 cursor-pointer relative",
                          // Padding based on collapsed state
                          isMobileOrTablet ? "px-3 sm:px-4 py-3 sm:py-4 justify-between" : isCollapsed ? "px-2 py-3 justify-center" : "px-3 sm:px-4 py-3 sm:py-4 justify-between",
                          "text-sm font-medium",
                          // Enhanced touch targets for mobile/tablet
                          isMobileOrTablet && "min-h-[48px] touch-manipulation",
                          isActive
                            ? "bg-sidebar-accent text-sidebar-accent-foreground shadow-sm"
                            : "text-sidebar-foreground hover:bg-sidebar-accent/50 hover:text-sidebar-accent-foreground active:bg-sidebar-accent/70"
                        )}
                        onClick={() => {
                          onClose();
                          // Add haptic feedback for mobile devices
                          if (isMobileOrTablet && 'vibrate' in navigator) {
                            navigator.vibrate(50);
                          }
                        }}
                        role="menuitem"
                        tabIndex={0}
                        aria-current={isActive ? 'page' : undefined}
                        aria-label={t(getTranslationKey(route.label))}
                        title={(!isMobileOrTablet && isCollapsed) ? t(getTranslationKey(route.label)) : undefined}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onClose();
                          }
                        }}
                      >
                        <div className={cn(
                          "flex items-center",
                          isMobileOrTablet ? "space-x-3 sm:space-x-4" : isCollapsed ? "" : "space-x-3 sm:space-x-4"
                        )}>
                          <div className={cn(
                            "flex items-center justify-center rounded-md transition-colors",
                            isMobileOrTablet ? "w-8 h-8 sm:w-9 sm:h-9" : isCollapsed ? "w-10 h-10" : "w-8 h-8 sm:w-9 sm:h-9",
                            isActive 
                              ? "bg-sidebar-accent-foreground/10 text-sidebar-accent-foreground" 
                              : "text-sidebar-muted-foreground group-hover:text-sidebar-accent-foreground"
                          )}>
                            {typeof route.icon === 'function' ? (
                              <route.icon className={cn(
                                isMobileOrTablet ? "w-4 h-4 sm:w-5 sm:h-5" : isCollapsed ? "w-5 h-5" : "w-4 h-4 sm:w-5 sm:h-5"
                              )} />
                            ) : (
                              React.createElement(route.icon, { 
                                className: cn(
                                  isMobileOrTablet ? "w-4 h-4 sm:w-5 sm:h-5" : isCollapsed ? "w-5 h-5" : "w-4 h-4 sm:w-5 sm:h-5"
                                )
                              })
                            )}
                          </div>
                          {(!isMobileOrTablet && !isCollapsed) && (
                            <span className="font-medium text-sm sm:text-base truncate">
                              {t(getTranslationKey(route.label))}
                            </span>
                          )}
                          {isMobileOrTablet && (
                            <span className="font-medium text-sm sm:text-base truncate">
                              {t(getTranslationKey(route.label))}
                            </span>
                          )}
                        </div>
                        {!isCollapsed && (
                          <div className="flex items-center space-x-2">
                            {route.badge && (
                              <Badge 
                                variant={typeof route.badge === 'number' ? 'destructive' : 'secondary'} 
                                className="text-xs"
                              >
                                {route.badge}
                              </Badge>
                            )}
                            {isActive && !isCollapsed && (
                              <ChevronRight className="w-4 h-4 text-sidebar-accent-foreground" />
                            )}
                          </div>
                        )}
                        {route.badge && isCollapsed && !isMobileOrTablet && (
                          <div className="absolute top-1 right-1 w-2 h-2 rounded-full bg-destructive" />
                        )}
                        {isActive && !isCollapsed && (
                          <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-primary rounded-r-full" />
                        )}
                        {isActive && isCollapsed && !isMobileOrTablet && (
                          <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-primary rounded-r-full" />
                        )}
                      </div>
                    </Link>
                  );
                })}
              </div>
            </nav>
          </ScrollArea>

          {/* Footer */}
          {!isMobileOrTablet && isCollapsed ? (
            <div className="border-t border-sidebar-border p-2">
              <div className="text-center">
                <div className="text-xs text-sidebar-muted-foreground">
                  <p className="text-[10px] opacity-75">© 2024</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="border-t border-sidebar-border p-3 sm:p-4">
              <div className="text-center">
                <div className="text-xs text-sidebar-muted-foreground space-y-1">
                  <p className="font-medium">{t("appName")}</p>
                  <p className="text-[10px] sm:text-xs">v1.0.0</p>
                  <p className="text-[9px] sm:text-[10px] opacity-75">© 2024 {t("badeel")}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}